import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';

/**
 * Mirrors PlatformRole in src/Platform.Domain/Enums.cs. The numbers matter: authorization is a
 * ladder, and the portal compares them the same way the API does.
 */
export const PlatformRole = {
  MspOwner: 100,
  MspAdmin: 90,
  ResellerAdmin: 60,
  TenantAdmin: 50,
  Editor: 30,
  Designer: 20,
  Viewer: 10,
} as const;

export type PlatformRoleValue = (typeof PlatformRole)[keyof typeof PlatformRole];

export interface RoleAssignment {
  role: PlatformRoleValue;
  /** Null means every tenant. Only ever set for MSP-level roles. */
  tenantId: string | null;
  tenantSlug: string | null;
  folderId: string | null;
}

/** Present when an OnBrand admin is viewing the portal scoped as one partner. */
export interface ViewAsPartner {
  id: string;
  name: string;
}

export interface Session {
  principalId: string;
  email: string;
  displayName: string | null;
  roleAssignments: RoleAssignment[];
  viewAsPartner?: ViewAsPartner | null;
  /** Partner books this principal administers. See API SessionResponse.PartnerBooks. */
  partnerBooks?: ViewAsPartner[];
}

/**
 * Lets the portal run against sample data with no API behind it.
 *
 * Front-end work — the signature designer especially — should not be blocked on the backend being
 * up, and a designer that cannot be opened cannot be reviewed. Enabled with `VITE_MOCK_SESSION=1`,
 * and only ever consulted when Vite is serving in development, so a production bundle has no path
 * to it regardless of how the environment is configured. The banner in the app shell exists so
 * nobody mistakes mock data for the real thing.
 */
export const isMockSession =
  import.meta.env.DEV && import.meta.env['VITE_MOCK_SESSION'] === '1';

const mockSession: Session = {
  principalId: '00000000-0000-0000-0000-0000000000de',
  email: 'dev@localhost',
  displayName: 'Development user',
  roleAssignments: [{ role: PlatformRole.MspOwner, tenantId: null, tenantSlug: null, folderId: null }],
};

/**
 * The signed-in user, or null when anonymous.
 *
 * A 401 is an expected answer here, not a failure — it is how the shell decides to show the sign-in
 * screen. A 404 is treated the same way on purpose: it means the API is up but has no `/api/me`,
 * which during this build means the endpoint has not shipped yet, and showing a sign-in prompt is
 * more useful than an error about a missing route. Anything else is a real fault and surfaces as one.
 */
export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: async (): Promise<Session | null> => {
      if (isMockSession) {
        return mockSession;
      }

      try {
        return await api.get<Session>('/api/me');
      } catch (error) {
        if (error instanceof ApiError && (error.isUnauthenticated || error.isNotFound)) {
          return null;
        }
        throw error;
      }
    },
    staleTime: Infinity,
    retry: false,

    // Somebody holding nothing is sitting on the awaiting-access screen while an administrator
    // grants them something in another window. With the session cached forever they would sit
    // there indefinitely and conclude the grant had not worked; polling only in that state costs
    // one request a minute for the few minutes it lasts, and nothing at all for everybody else.
    refetchInterval: (query) =>
      (query.state.data?.roleAssignments.length ?? 0) === 0 ? 30_000 : false,
  });
}

/** Which ways in this deployment offers. Mirrors SignInProvidersResponse. */
export interface SignInProviders {
  microsoft: boolean;
  google: boolean;
}

/**
 * What the sign-in screen should show.
 *
 * A deployment may have Microsoft, Google, both, or neither, and only the API knows which clients
 * are configured. Asking is what stops the screen offering a button that ends in a 503.
 *
 * Cached for the session: providers change when somebody edits configuration and restarts the API,
 * which is not something to poll for. A failure answers "neither", because a sign-in screen that
 * renders nothing is more honest than one whose buttons do not work.
 */
export function useSignInProviders() {
  return useQuery({
    queryKey: ['sign-in-providers'],
    queryFn: async (): Promise<SignInProviders> => {
      if (isMockSession) {
        return { microsoft: true, google: true };
      }

      try {
        return await api.get<SignInProviders>('/api/auth/providers');
      } catch {
        return { microsoft: false, google: false };
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

/** True when the session carries a role at or above `minimum` for the given tenant. */
export function hasRole(
  session: Session | null | undefined,
  minimum: PlatformRoleValue,
  tenantId?: string,
): boolean {
  if (!session) {
    return false;
  }

  return session.roleAssignments.some((assignment) => {
    if (assignment.role < minimum) {
      return false;
    }

    // A platform-wide grant covers every tenant, but only MSP roles are ever issued that way.
    if (assignment.tenantId === null) {
      return assignment.role >= PlatformRole.MspAdmin;
    }

    return tenantId !== undefined && assignment.tenantId === tenantId;
  });
}

/** True when the user administers the platform itself rather than a single customer. */
export function isMspStaff(session: Session | null | undefined): boolean {
  return (
    session?.roleAssignments.some(
      (a) => a.tenantId === null && a.role >= PlatformRole.MspAdmin,
    ) ?? false
  );
}

/**
 * True when the session can mint a new tenant.
 *
 * MSP staff can, unless they are viewing as a partner — then they create into that partner's book
 * the same way a ResellerAdmin would. Partners (real or viewed-as) can when they have a book.
 */
export function canCreateTenants(session: Session | null | undefined): boolean {
  if (!session) {
    return false;
  }

  if (session.viewAsPartner) {
    return true;
  }

  if ((session.partnerBooks?.length ?? 0) > 0) {
    return true;
  }

  return isMspStaff(session);
}
